import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Lightbulb, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us"
        description="Franchise India Knowledge Services is part of the Franchise India Group — Asia's largest franchise solutions organisation. We specialise exclusively in knowledge-based business franchising."
        path="/about"
      />
      {/* HEADER */}
      <section className="bg-navy noise-overlay text-white pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel className="text-gold">About Us</SectionLabel>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl text-balance">
              The world's only franchise consultancy for knowledge businesses.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionLabel>Who We Are</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy">
              Part of the Franchise India Group.
            </h2>
            <div className="mt-6 border border-navy/10 bg-white inline-flex px-6 py-4">
              <img
                src="https://customer-assets.emergentagent.com/job_franchise-asia/artifacts/lg6jivtt_logos%20%281%29.webp"
                alt="Franchise India — Ideas For Tomorrow"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </div>
            <div className="mt-6 space-y-5 text-lg text-ink-secondary leading-relaxed">
              <p>
                Franchise India Knowledge Services (FIKS) is proud to be a part of
                the <strong className="text-navy">Franchise India Group</strong> —
                Asia's largest and most respected integrated franchise and retail
                solutions organisation.
              </p>
              <p>
                Backed by decades of franchising expertise, we focus exclusively on
                knowledge-based businesses: coaching, training &amp; consulting,
                child experience, child education, franchise consulting and sports
                education.
              </p>
              <p>
                Our mission is simple — help professionals set up profitable,
                scalable businesses, and help proven brands become franchise-ready
                and expand across global markets.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -top-5 -left-5 h-28 w-28 bg-gold z-0" />
              <img src={ABOUT_IMG} alt="Franchise India Group" className="relative z-10 w-full h-[480px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28 bg-canvas-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>What Drives Us</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy max-w-2xl">
              Values that shape every partnership.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lightbulb, t: "Knowledge First", d: "We specialise only in knowledge businesses — and know them deeply." },
              { icon: Globe2, t: "Global Reach", d: "A network that spans global markets and beyond." },
              { icon: ShieldCheck, t: "Trusted Partner", d: "Mandated by world-class brands to represent them." },
              { icon: Sparkles, t: "Growth Obsessed", d: "Relentlessly focused on profitable, scalable expansion." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="bg-white border border-navy/10 p-8 h-full">
                  <div className="h-12 w-12 flex items-center justify-center bg-navy text-gold">
                    <v.icon size={22} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-navy">{v.t}</h3>
                  <p className="mt-2 text-ink-secondary">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-dark text-white py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-semibold">Let's grow together.</h2>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-8 inline-flex items-center gap-2 bg-gold text-navy px-10 py-4 font-bold hover:bg-yellow-400 transition-colors group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
